// Target Group observability analytics service (BOF-65).
// Structural metrics come from accountApi (ACM-51), financial from pricingApi (PRICE-85).
// See specs/001-target-group-dashboard/contracts/ for endpoint contracts.
//
// The API returns a generic metrics[] array; this module maps it to typed view-model
// objects so the rest of the app never deals with raw metric payloads.

import { accountApi, pricingApi } from "../config/apiConfig";
import type {
  AnalyticsScope,
  PeriodRange,
  MonthKey,
  HistogramBucket,
  Ratio,
  Money,
  RawMetric,
  RawMetricGroupResponse,
  RawDynamicsSeriesResponse,
  CountValue,
  ScalarValue,
  RatioValue,
  DistributionValue,
  RankingValue,
  MoneyValue,
  MoneyBreakdownValue,
  OverviewAnalytics,
  DynamicsAnalytics,
  MembershipDynamicsPoint,
  FinancialAnalytics,
  SavingsBreakdownRow,
} from "../types/targetGroupAnalytics";

// --- Helpers ---

const monthKeyToString = (m: MonthKey): string =>
  `${m.year}-${String(m.month).padStart(2, "0")}`;

const findCount = (metrics: RawMetric[], id: string): number =>
  ((metrics.find((m) => m.id === id)?.value as CountValue | undefined)?.count ?? 0);

const findScalar = (metrics: RawMetric[], id: string): number =>
  ((metrics.find((m) => m.id === id)?.value as ScalarValue | undefined)?.value ?? 0);

const findRatio = (metrics: RawMetric[], id: string): Ratio | undefined => {
  const v = metrics.find((m) => m.id === id)?.value as RatioValue | undefined;
  return v ? { numerator: v.numerator, denominator: v.denominator } : undefined;
};

const findBuckets = (metrics: RawMetric[], id: string): HistogramBucket[] =>
  ((metrics.find((m) => m.id === id)?.value as DistributionValue | undefined)?.buckets ?? []);

const findRankingEntries = (metrics: RawMetric[], id: string) =>
  ((metrics.find((m) => m.id === id)?.value as RankingValue | undefined)?.entries ?? []);

const findMoney = (metrics: RawMetric[], id: string): Money => {
  const v = metrics.find((m) => m.id === id)?.value as MoneyValue | undefined;
  return v ? { amount: v.amount, currency: v.currency } : { amount: 0, currency: "EUR" };
};

const findBreakdown = (metrics: RawMetric[], id: string): SavingsBreakdownRow[] => {
  const v = metrics.find((m) => m.id === id)?.value as MoneyBreakdownValue | undefined;
  return (v?.entries ?? []).map((e) => ({
    key: e.key,
    label: e.label,
    amount: { amount: e.amount.amount, currency: e.amount.currency },
    sessionCount: e.session_count,
  }));
};

// --- Mapping functions ---

function mapOverview(raw: RawMetricGroupResponse): OverviewAnalytics {
  const { metrics } = raw;

  const stationsPerGroupBuckets = findBuckets(metrics, "stations_per_group");
  const tariffBuckets = findBuckets(metrics, "tariff_adoption");
  const rankingEntries = findRankingEntries(metrics, "top_owners");
  const usersRatio = findRatio(metrics, "users_using_target_groups");
  const stationsRatio = findRatio(metrics, "stations_with_target_groups");

  return {
    period: raw.period,
    isCurrentMonth: raw.is_current_month,
    source: raw.source,
    adoption: {
      ownersWithGroups: findCount(metrics, "owners_with_groups"),
      ownersWithEmptyGroups: findCount(metrics, "owners_with_empty_groups"),
      totalGroups: findCount(metrics, "total_groups"),
      stationsCovered: findCount(metrics, "stations_covered"),
    },
    usageRatios: {
      ...(usersRatio ? { usersUsingTargetGroups: usersRatio } : {}),
      stationsWithTargetGroups: stationsRatio ?? { numerator: 0, denominator: null },
    },
    distributions: {
      groupsPerOwner: findBuckets(metrics, "groups_per_owner"),
      membersPerGroup: findBuckets(metrics, "members_per_group"),
    },
    composition: {
      stationsPerGroup: {
        single: stationsPerGroupBuckets.find((b) => b.label === "single")?.count ?? 0,
        multi: stationsPerGroupBuckets.find((b) => b.label === "multi")?.count ?? 0,
      },
    },
    tariff: {
      withTariff: tariffBuckets.find((b) => b.label === "with_tariff")?.count ?? 0,
      withoutTariff: tariffBuckets.find((b) => b.label === "without_tariff")?.count ?? 0,
    },
    ranking:
      rankingEntries.length > 0
        ? {
            topOwners: rankingEntries.map((e) => ({
              key: e.key,
              label: e.label,
              memberCount: e.value,
            })),
          }
        : undefined,
  };
}

function mapDynamicsPoint(raw: RawMetricGroupResponse): MembershipDynamicsPoint {
  const { metrics } = raw;
  return {
    period: raw.period,
    isCurrentMonth: raw.is_current_month,
    source: raw.source,
    joins: findCount(metrics, "joins"),
    leaves: findCount(metrics, "leaves"),
    netChange: findCount(metrics, "net_change"),
    activeMembers: findCount(metrics, "active_members"),
    newGroups: findCount(metrics, "new_groups"),
    avgMembershipDays: findScalar(metrics, "avg_membership_days"),
    memberRetention: findCount(metrics, "member_retention"),
  };
}

function mapDynamics(raw: RawDynamicsSeriesResponse): DynamicsAnalytics {
  return {
    from: raw.from,
    to: raw.to,
    series: raw.series.map(mapDynamicsPoint),
  };
}

// --- Public API ---

export const getOverviewAnalytics = async (
  scope: AnalyticsScope,
  month: MonthKey,
): Promise<OverviewAnalytics> => {
  const endpoint =
    scope.kind === "owner"
      ? "/target-groups/analytics/me/overview"
      : "/target-groups/analytics/overview";
  const response = await accountApi.get<RawMetricGroupResponse>(endpoint, {
    params: { month: monthKeyToString(month) },
  });
  return mapOverview(response.data);
};

export const getDynamicsAnalytics = async (
  scope: AnalyticsScope,
  period: PeriodRange,
): Promise<DynamicsAnalytics> => {
  const endpoint =
    scope.kind === "owner"
      ? "/target-groups/analytics/me/dynamics"
      : "/target-groups/analytics/dynamics";
  const response = await accountApi.get<RawDynamicsSeriesResponse>(endpoint, {
    params: {
      from: monthKeyToString(period.from),
      to: monthKeyToString(period.to),
    },
  });
  return mapDynamics(response.data);
};

export const getFinancialAnalytics = async (
  scope: AnalyticsScope,
  month: MonthKey,
): Promise<FinancialAnalytics> => {
  const endpoint =
    scope.kind === "owner"
      ? "/analytics/target-groups/financials/me/overview"
      : "/analytics/target-groups/financials/overview";
  const response = await pricingApi.get<RawMetricGroupResponse>(endpoint, {
    params: { month: monthKeyToString(month) },
  });
  return mapFinancial(response.data);
};

function mapFinancial(raw: RawMetricGroupResponse): FinancialAnalytics {
  const { metrics } = raw;
  return {
    period: raw.period,
    isCurrentMonth: raw.is_current_month,
    source: raw.source,
    moneySavedTotal: findMoney(metrics, "money_saved_total"),
    averageSavedPerMember: findMoney(metrics, "average_saved_per_member"),
    distinctMemberCount: findCount(metrics, "distinct_member_count"),
    targetGroupSessionCount: findCount(metrics, "target_group_session_count"),
    targetGroupCdrShare: findRatio(metrics, "target_group_cdr_share") ?? { numerator: 0, denominator: null },
    negativeSavingSessions: findCount(metrics, "negative_saving_sessions"),
    negativeSavingAmount: findMoney(metrics, "negative_saving_amount"),
    excludedNoBaselineCount: findCount(metrics, "excluded_no_baseline_count"),
    savingsByGroup: findBreakdown(metrics, "savings_by_group"),
    savingsByMember: findBreakdown(metrics, "savings_by_member"),
  };
}
